import React from "react";
import { Typography } from "@mui/material";
import { Select, TextField } from "@outpave/ds";
import { Inline } from "components/layout/reusableLayouts";
import SettingsRow from "./SettingsRow";
import { useTranslation } from "react-i18next";
import { GeneralSettingsTabProps } from "hooks/useCompanySettings";
import { FormikProps } from "formik";
import { titleCase } from "title-case";
import { MileageReimbursementType } from "enums/MileageReimbursementType";

interface GeneralSettingsProps {
  editMode: boolean;
  generalFormik: FormikProps<GeneralSettingsTabProps>;
  defaultMileageData: {
    getDefaultMileageRates: {
      highMileageWarningValue: number;
      mileageRates: { type: string; rate: number }[];
    };
  };
}

/**
 * The rows this tab renders, each entry saying whether that row has a
 * description, so a loading skeleton can match what replaces it.
 */
export const GENERAL_SKELETON_ROWS = [true];

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  editMode,
  generalFormik,
  defaultMileageData,
}) => {
  const { t } = useTranslation();

  const generalValues = generalFormik?.values;
  const defaultMileageValues =
    defaultMileageData?.getDefaultMileageRates.mileageRates;
  const highMileageWarningValue =
    defaultMileageData?.getDefaultMileageRates.highMileageWarningValue;

  const rateAboveWarning =
    (generalValues?.mileageReimbursement?.rate ?? 0) > highMileageWarningValue;

  const handleTypeChange = (value: string | null) => {
    const type = value ?? "";
    generalFormik.setFieldValue("mileageReimbursement.type", type);
    if (type !== MileageReimbursementType.CUSTOM) {
      const defaultRate =
        defaultMileageValues.find((item) => item.type === type)?.rate || 0;
      generalFormik.setFieldValue("mileageReimbursement.rate", defaultRate);
    } else {
      generalFormik.setFieldValue("mileageReimbursement.rate", 0);
    }
  };

  return (
    <>
      {generalValues && defaultMileageValues && (
        <SettingsRow
          label={t("Mileage Rate")}
          description={t("Rate in which mileage reimbursement is calculated")}
        >
          {editMode ? (
            <Inline gap="var(--space-5)" align="stretch" wrap={false}>
              <Select
                fullWidth
                size="md"
                clearable={false}
                label={t("Type")}
                value={generalValues.mileageReimbursement.type}
                options={defaultMileageValues.map((item) => ({
                  id: item.type,
                  label: t(titleCase(item.type.toLowerCase())),
                }))}
                onChange={handleTypeChange}
              />

              <TextField
                fullWidth
                type="number"
                // Scrolling over a focused number input otherwise changes the
                // saved rate silently, as it would on any other number field.
                inputProps={{
                  min: 0.01,
                  step: 0.01,
                  onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                    event.currentTarget.blur(),
                }}
                label={t("Rate")}
                size="md"
                value={generalValues.mileageReimbursement.rate}
                onChange={(e) =>
                  generalFormik.setFieldValue(
                    "mileageReimbursement.rate",
                    parseFloat(e.target.value || "0"),
                  )
                }
                disabled={
                  generalValues.mileageReimbursement.type !==
                  MileageReimbursementType.CUSTOM
                }
                errorText={
                  rateAboveWarning
                    ? t("HighMileageReimbursementRate")
                    : undefined
                }
              />
            </Inline>
          ) : (
            <Inline gap="var(--space-3)" align="stretch" wrap={false}>
              <Typography variant="body1">
                {t(
                  titleCase(
                    generalValues.mileageReimbursement.type.toLowerCase(),
                  ),
                )}
              </Typography>
              <Typography variant="body1">
                {generalValues.mileageReimbursement.rate} / {t("mile")}
              </Typography>
            </Inline>
          )}
        </SettingsRow>
      )}
    </>
  );
};

export default GeneralSettings;
